import type { FairshareConfig } from "@fluid-app/fairshare";
import {
  checkout,
  EventTypes,
  getCart,
  getLocalCart,
  initializeFluid as originalInitializeFluid,
  removeCartItem,
  updateCartItems,
} from "@fluid-app/fluid";

// Export FluidConfig type
export type FluidConfig = FairshareConfig;

// Define a type for our custom window properties
export interface CustomWindow extends Window {
  FLUID_SDK_INITIALIZED?: boolean;
  __FLUID_SDK_READY_PROMISE?: Promise<void>;
  __FLUID_SDK_READY?: () => void;
  __FLUID_SDK?: FluidSDK;
  // Add global function properties that might be exposed
  getLocalCart?: typeof getLocalCart;
  getCart?: typeof getCart;
  updateCartItems?: typeof updateCartItems;
  removeCartItem?: typeof removeCartItem;
  checkout?: typeof checkout;
  EventTypes?: typeof EventTypes;
}

// Define a type for the FluidSDK module
export interface FluidSDK {
  getLocalCart: typeof getLocalCart;
  getCart: typeof getCart;
  updateCartItems: typeof updateCartItems;
  removeCartItem: typeof removeCartItem;
  checkout: typeof checkout;
  EventTypes: typeof EventTypes;
}

/**
 * Wait for the SDK to be initialized
 * This handles various ways the SDK might be initialized
 */
export const waitForSdkReady = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  const customWindow = window as CustomWindow;

  // Check if SDK is already marked as initialized
  if (customWindow.FLUID_SDK_INITIALIZED === true) {
    console.log("SDK already initialized, resolving immediately");
    return Promise.resolve();
  }

  // Check if there's a ready promise available from the bundle
  if (customWindow.__FLUID_SDK_READY_PROMISE) {
    console.log("Using existing SDK ready promise");
    return customWindow.__FLUID_SDK_READY_PROMISE;
  }

  // Create a new promise if none exists
  console.log("Creating new SDK ready promise");

  // Check if we can detect the SDK functions directly
  if (
    customWindow.__FLUID_SDK ||
    (typeof checkout === "function" &&
      typeof getCart === "function" &&
      typeof updateCartItems === "function")
  ) {
    console.log("SDK functions detected, considering SDK ready");
    customWindow.FLUID_SDK_INITIALIZED = true;
    return Promise.resolve();
  }

  const readyPromise = new Promise<void>((resolve) => {
    // Set a global callback that main.js can call when SDK is ready
    customWindow.__FLUID_SDK_READY = () => {
      console.log("SDK ready callback called");
      customWindow.FLUID_SDK_INITIALIZED = true;
      resolve();
    };

    // Also check periodically in case the callback was missed
    const checkInterval = setInterval(() => {
      // Check for SDK initialization flag
      if (customWindow.FLUID_SDK_INITIALIZED === true) {
        console.log("SDK detected as initialized via interval");
        clearInterval(checkInterval);
        resolve();
        return;
      }

      // Check for direct function availability
      if (
        customWindow.__FLUID_SDK ||
        (typeof checkout === "function" &&
          typeof getCart === "function" &&
          typeof updateCartItems === "function")
      ) {
        console.log("SDK functions detected in interval check");
        customWindow.FLUID_SDK_INITIALIZED = true;
        clearInterval(checkInterval);
        resolve();
        return;
      }
    }, 100);

    // Set a timeout to prevent waiting forever
    setTimeout(() => {
      clearInterval(checkInterval);
      console.warn("Timed out waiting for Fluid SDK initialization");

      // If we can access the functions directly, consider SDK ready even after timeout
      if (
        customWindow.__FLUID_SDK ||
        (typeof checkout === "function" &&
          typeof getCart === "function" &&
          typeof updateCartItems === "function")
      ) {
        console.log("SDK functions available after timeout, proceeding");
        customWindow.FLUID_SDK_INITIALIZED = true;
      }

      resolve(); // Resolve anyway to prevent hanging
    }, 5000);
  });

  // Store the promise for reuse
  customWindow.__FLUID_SDK_READY_PROMISE = readyPromise;
  return readyPromise;
};

/**
 * Get the SDK instance from the window or via imports
 */
export const getSDK = (): FluidSDK => {
  if (typeof window !== "undefined") {
    const customWindow = window as CustomWindow;

    // First try to get the __FLUID_SDK global instance
    if (customWindow.__FLUID_SDK) {
      console.log("Using globally shared SDK instance");

      // Ensure all required methods are available
      if (
        typeof customWindow.__FLUID_SDK.getLocalCart === "function" &&
        typeof customWindow.__FLUID_SDK.getCart === "function" &&
        typeof customWindow.__FLUID_SDK.updateCartItems === "function" &&
        typeof customWindow.__FLUID_SDK.removeCartItem === "function" &&
        typeof customWindow.__FLUID_SDK.checkout === "function"
      ) {
        return {
          getLocalCart: customWindow.__FLUID_SDK.getLocalCart.bind(
            customWindow.__FLUID_SDK,
          ),
          getCart: customWindow.__FLUID_SDK.getCart.bind(
            customWindow.__FLUID_SDK,
          ),
          updateCartItems: customWindow.__FLUID_SDK.updateCartItems.bind(
            customWindow.__FLUID_SDK,
          ),
          removeCartItem: customWindow.__FLUID_SDK.removeCartItem.bind(
            customWindow.__FLUID_SDK,
          ),
          checkout: customWindow.__FLUID_SDK.checkout.bind(
            customWindow.__FLUID_SDK,
          ),
          EventTypes: customWindow.__FLUID_SDK.EventTypes,
        };
      }
    }

    // Next, try to access the functions directly if they're in the global scope
    if (
      customWindow.getLocalCart &&
      customWindow.getCart &&
      customWindow.updateCartItems &&
      customWindow.removeCartItem &&
      customWindow.checkout &&
      customWindow.EventTypes
    ) {
      console.log("Using globally exposed SDK functions");
      return {
        getLocalCart: customWindow.getLocalCart.bind(customWindow),
        getCart: customWindow.getCart.bind(customWindow),
        updateCartItems: customWindow.updateCartItems.bind(customWindow),
        removeCartItem: customWindow.removeCartItem.bind(customWindow),
        checkout: customWindow.checkout.bind(customWindow),
        EventTypes: customWindow.EventTypes,
      };
    }
  }

  // Fall back to the imported functions
  console.log("Using imported SDK functions");
  return {
    getLocalCart,
    getCart,
    updateCartItems,
    removeCartItem,
    checkout,
    EventTypes,
  };
};

// Pre-cache SDK for faster access
let cachedSDK: FluidSDK | null = null;

/**
 * Get the cached SDK instance or create a new one
 */
export const getAndCacheSDK = (): FluidSDK => {
  cachedSDK ??= getSDK();
  return cachedSDK;
};

/**
 * Get initial cart data from local storage
 */
export const initializeWithLocalCart = (): ReturnType<
  typeof getLocalCart
> | null => {
  try {
    // First try to get the SDK
    const sdk = getAndCacheSDK();

    // Check if SDK is initialized before attempting to get local cart
    // This is needed because the CartWidget might be rendered before the SDK is initialized
    if (typeof window !== "undefined") {
      const customWindow = window as CustomWindow;
      if (!customWindow.FLUID_SDK_INITIALIZED) {
        // SDK not initialized yet, return null without error
        return null;
      }
    }

    // Then try to get local cart data
    return sdk.getLocalCart();
  } catch (e) {
    console.error("Failed to get initial local cart:", e);
    return null;
  }
};

/**
 * Re-export the initializeFluid function from the @fluid-app/fluid package
 * This function initializes the Fluid SDK with the given configuration
 */
export const initializeFluid = (config: FluidConfig): void => {
  // Initialize the global window properties
  if (typeof window !== "undefined") {
    const customWindow = window as CustomWindow;

    // Call the original function
    originalInitializeFluid(config);

    // Mark the SDK as initialized
    customWindow.FLUID_SDK_INITIALIZED = true;

    // Store the SDK functions on the window
    customWindow.__FLUID_SDK = getSDK();

    // Call the ready callback if it exists
    if (typeof customWindow.__FLUID_SDK_READY === "function") {
      customWindow.__FLUID_SDK_READY();
    }
  } else {
    // For SSR environments, just call the original function
    originalInitializeFluid(config);
  }
};
